import React, { useState, useEffect, useContext, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import OpenSeadragon from "openseadragon";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import { LazyLoadImage } from "react-lazy-load-image-component";
import "react-lazy-load-image-component/src/effects/blur.css";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import SweetAlert from "react-bootstrap-sweetalert";
import Header from "../../../components/dashboard/header";
import Footer from "../../../components/dashboard/footer";
import SideMenu from "../../student/SideMenu";
import Loader from "../../../components/loader";
import SessionPopup from "../../SessionPopup";
import { contentContext } from "../../../context/contentContext";
import progessIcon from "../../../assets/images/gifs/process-icon.gif";
import loadingIcon from "../../../assets/images/gifs/uploading-icon.gif";
import errorSvg from "../../../assets/images/gifs/errorSvg.svg";
import deepZoom from "../../../assets/images/icons/Dna-dz.svg";

let { lambda, appname } = window.app;

const STALE_TTL_MS = 4 * 60 * 60 * 1000;

const STATUS_COLORS = {
    "Draft":                       { background: "#6c757d" },
    "Submitted":                   { background: "#0d6efd" },
    "Pending Payment":             { background: "#dc3545" },
    "Payment Received":            { background: "#198754" },
    "Under Lab Review":            { background: "#0dcaf0", color: "#000" },
    "Assigned to Pathologist":     { background: "#ffc107", color: "#000" },
    "Additional Test Requested":   { background: "#fd7e14" },
    "Additional Test In Progress": { background: "#6f42c1" },
    "Reassigned to Pathologist":   { background: "#20c997", color: "#000" },
    "Completed":                   { background: "#198754" },
    "Converted to Learning Case":  { background: "#0a58ca" },
};

// ── helpers ──────────────────────────────────────────────────────────────────

function getExtension(filename) {
    if (!filename) return null;
    const ext = filename.split(".").pop().toLowerCase();
    return ext === "tiff" || ext === "tif" ? ".tif,.tiff" : ".jpeg,.jpg,.png";
}

function isStaleUpload(file) {
    if (!file || file.uploadStatus !== "INPROGRESS") return false;
    const lastSeen = new Date(file.lastSeenAt || file.fileCreated || Date.now()).getTime();
    return Date.now() - lastSeen > STALE_TTL_MS;
}

function checkStuckUploads(file) {
    if (!file) return false;
    const created = new Date(file.fileCreated);
    const diffMins = (Date.now() - created) / 1000 / 60;
    return file.uploadStatus === "INPROGRESS" && diffMins > 30;
}

// ─────────────────────────────────────────────────────────────────────────────

export default function TeachingAdminBiopsyView() {
    const { id } = useParams();
    const navigate = useNavigate();
    const token = localStorage.getItem("token");
    const teachingAdminUserId = localStorage.getItem("userId");

    const { GetTimeActivity } = useContext(contentContext);

    // ── case data ───────────────────────────────────────────────────────────
    const [caseDetails, setCaseDetails] = useState(null);
    const [imageCloudfront, setImageCloudfront] = useState("");

    // ── convert to learning case ────────────────────────────────────────────
    const [isConverting, setIsConverting] = useState(false);
    const [showConvertConfirm, setShowConvertConfirm] = useState(false);
    const [convertSuccessPopup, setConvertSuccessPopup] = useState(false);

    // ── DZI / OpenSeadragon ─────────────────────────────────────────────────
    const viewerRef = useRef(null);
    const transformRef = useRef(null);
    const [isPopupOpen, setPopupOpen] = useState(false);
    const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 });
    const [dziUrl, setDziUrl] = useState("");
    const [dziUrlFile, setDziUrlFile] = useState("");
    const [dzipopupImage, setDZIPopupImage] = useState("");
    const [isPopupLoading, setPopupLoading] = useState(false);
    const [viewer, setViewer] = useState(null);
    const [dziImageList, setDziImageList] = useState([]);
    const [dziCurrentIndex, setDziCurrentIndex] = useState(0);
    const [isDziLoading, setIsDziLoading] = useState(false);

    // ── UI state ────────────────────────────────────────────────────────────
    const [isLoading, setIsLoading] = useState(true);
    const [showSessionPopup, setShowSessionPopup] = useState(false);
    const [showImagePopup, setShowImagePopup] = useState(false);
    const [popupImageUrl, setPopupImageUrl] = useState(null);
    const [clinicalPopupList, setClinicalPopupList] = useState([]);
    const [clinicalPopupIndex, setClinicalPopupIndex] = useState(0);
    const [isClinicalLoading, setIsClinicalLoading] = useState(false);

    // ── init ────────────────────────────────────────────────────────────────
    useEffect(() => {
        if (!token) { navigate("/"); return; }
        if (window.site?.common?.imageCloudfront) {
            setImageCloudfront(window.site.common.imageCloudfront.replace(/^http:\/\//i, "https://"));
        } else {
            setTimeout(() => {
                if (window.site?.common?.imageCloudfront) {
                    setImageCloudfront(window.site.common.imageCloudfront.replace(/^http:\/\//i, "https://"));
                }
            }, 1000);
        }
        fetchCase();
    }, []);

    // ── OSD viewer creation ─────────────────────────────────────────────────
    useEffect(() => {
        if (!isPopupOpen || !dziUrl || !viewerRef.current) return;

        const isDziURL = dziUrl.includes("output.dzi");
        const { width, height } = imageDimensions;
        const tileSource = isDziURL
            ? { Image: { xmlns: "http://schemas.microsoft.com/deepzoom/2008", Url: encodeURI(dziUrl), Format: "jpeg", Overlap: 2, TileSize: 256, Size: { Width: width, Height: height } } }
            : { type: "image", url: encodeURI(dziUrl), buildPyramid: false };

        const osdViewer = OpenSeadragon({
            id: "openseadragon-viewer",
            prefixUrl: "https://openseadragon.github.io/openseadragon/images/",
            tileSources: tileSource,
            showNavigator: true,
            crossOriginPolicy: "Anonymous",
            navigatorPosition: "BOTTOM_RIGHT",
            maxZoomPixelRatio: 10,
            minZoomImageRatio: 0.5,
            visibilityRatio: 1,
            zoomPerScroll: 2,
            homeFillsViewer: true,
            gestureSettingsMouse: { clickToZoom: true, dblClickToZoom: true, flickEnabled: true, pinchToZoom: true, scrollToZoom: true },
            showZoomControl: true,
            showHomeControl: true,
            showFullPageControl: true,
            showRotationControl: true,
            wrapHorizontal: false,
        });
        setViewer(osdViewer);
        return () => { if (osdViewer) osdViewer.destroy(); };
    }, [dziUrl, imageDimensions]);

    // ── keyboard navigation for DZI popup ──────────────────────────────────
    useEffect(() => {
        if (!isPopupOpen || dziImageList.length <= 1) return;
        const onKey = (e) => {
            if (e.key === "ArrowLeft") navigateDziImage(dziCurrentIndex - 1);
            if (e.key === "ArrowRight") navigateDziImage(dziCurrentIndex + 1);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [isPopupOpen, dziImageList, dziCurrentIndex, viewer]);

    // ── keyboard navigation for clinical popup ──────────────────────────────
    useEffect(() => {
        if (!showImagePopup || clinicalPopupList.length <= 1) return;
        const onKey = (e) => {
            if (e.key === "ArrowLeft") navigateClinicalImage(clinicalPopupIndex - 1);
            if (e.key === "ArrowRight") navigateClinicalImage(clinicalPopupIndex + 1);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [showImagePopup, clinicalPopupList, clinicalPopupIndex]);

    // ── data fetching ────────────────────────────────────────────────────────

    const fetchCase = () => {
        setIsLoading(true);
        axios.get(`${lambda}/biopsyCaseInfo?appname=${appname}&patientid=${id}&token=${token}`)
            .then((res) => {
                if (res?.data?.result === "Invalid token or Expired") {
                    setShowSessionPopup(true);
                } else if (res?.data?.result?.length > 0) {
                    setCaseDetails(res.data.result[0]);
                }
                setIsLoading(false);
            })
            .catch(() => setIsLoading(false));
    };

    const totalAdditionalFee = (caseDetails?.additionalTests || []).reduce((sum, t) => sum + (parseFloat(t.fee) || 0), 0);

    // ── convert to learning case ────────────────────────────────────────────

    const handleConvertToLearningCase = () => {
        GetTimeActivity();
        setShowConvertConfirm(true);
    };

    const confirmConvertToLearningCase = async () => {
        setShowConvertConfirm(false);
        setIsConverting(true);
        try {
            const res = await axios.post(
                `${lambda}/convertBiopsytolearning?appname=${appname}&token=${token}&userid=${teachingAdminUserId}&patientid=${id}`
            );
            if (res?.data?.result === "Converted Successfully") {
                setCaseDetails((prev) => ({ ...prev, status: "Converted to Learning Case" }));
                setConvertSuccessPopup(true);
            } else {
                toast.error("Failed to convert case. Please try again.");
            }
        } catch (err) {
            console.error("Convert to Learning Case error:", err);
            toast.error("Failed to convert case. Please try again.");
        } finally {
            setIsConverting(false);
        }
    };

    // ── DZI helpers ──────────────────────────────────────────────────────────

    const buildTileSourceForFile = (file) => {
        let path = file.dziUrl || "";
        if (path.startsWith("/")) path = path.slice(1);
        const isDziURL = path.includes("output.dzi");
        if (isDziURL) {
            return {
                Image: {
                    xmlns: "http://schemas.microsoft.com/deepzoom/2008",
                    Url: encodeURI(imageCloudfront + path + "_files/"),
                    Format: "jpeg",
                    Overlap: 2,
                    TileSize: 256,
                    Size: { Width: file.width, Height: file.height },
                },
            };
        }
        return {
            type: "image",
            url: encodeURI(`${imageCloudfront}${(file.sourcepath || "").replace(/ /g, "%20")}?auto=compress,format`),
            buildPyramid: false,
        };
    };

    const openDziPopup = (file, filesList) => {
        setPopupOpen(true);
        setImageDimensions({ width: file.width, height: file.height });
        let path = file.dziUrl || "";
        if (path.startsWith("/")) path = path.slice(1);
        const isDziURL = path.includes("output.dzi");
        if (isDziURL) {
            setDziUrl(imageCloudfront + path + "_files/");
        } else {
            setDziUrl(`${imageCloudfront}${(file.sourcepath || "").replace(/ /g, "%20")}?auto=compress,format`);
        }
        setDziUrlFile(file.originalFilename || file.filename);
        setDZIPopupImage(imageCloudfront + file.sourcepath);
        setPopupLoading(true);
        if (filesList?.length) {
            setDziImageList(filesList);
            setDziCurrentIndex(filesList.findIndex((f) => f.fileid === file.fileid));
        }
    };

    const navigateDziImage = (newIndex) => {
        if (!dziImageList.length) return;
        const clamped = Math.max(0, Math.min(newIndex, dziImageList.length - 1));
        const file = dziImageList[clamped];
        if (!file) return;
        const tileSource = buildTileSourceForFile(file);
        if (viewer) {
            setIsDziLoading(true);
            viewer.open(tileSource);
            viewer.viewport.setRotation(0);
            viewer.addOnceHandler("open", () => setIsDziLoading(false));
        }
        setDziCurrentIndex(clamped);
        setDziUrlFile(file.originalFilename || file.filename);
        setDZIPopupImage(imageCloudfront + file.sourcepath);
    };

    const closeDeepZoom = () => {
        setPopupOpen(false);
        setDziUrl("");
        setDziUrlFile("");
        setImageDimensions({ width: 0, height: 0 });
        setDziImageList([]);
        setDziCurrentIndex(0);
        if (viewer) { viewer.destroy(); setViewer(null); }
    };

    const navigateClinicalImage = (newIndex) => {
        if (!clinicalPopupList.length) return;
        const clamped = Math.max(0, Math.min(newIndex, clinicalPopupList.length - 1));
        setIsClinicalLoading(true);
        setPopupImageUrl(clinicalPopupList[clamped]);
        setClinicalPopupIndex(clamped);
    };

    const openClinicalPopup = (list, index) => {
        setShowImagePopup(true);
        setIsClinicalLoading(true);
        setPopupImageUrl(list[index]);
        setClinicalPopupList(list);
        setClinicalPopupIndex(index);
    };

    // ── render helpers ───────────────────────────────────────────────────────

    const readOnlyField = (label, value) => (
        <div className="col-lg-4 col-md-6 col-sm-12">
            <div className="mb-3 input-field">
                <label className="form_label">{label}</label>
                <input type="text" className="form-control" disabled value={value || ""} />
            </div>
        </div>
    );

    const renderStatusBadge = (status) => {
        if (!status) return null;
        const style = STATUS_COLORS[status] || { background: "#6c757d" };
        return (
            <span className="ms-3" style={{ ...style, color: style.color || "#fff", fontSize: "12px", fontWeight: "600", padding: "3px 10px", borderRadius: "4px" }}>
                {status}
            </span>
        );
    };

    // ── render ───────────────────────────────────────────────────────────────

    if (isLoading) {
        return (
            <div id="layout-wrapper">
                <Header /><SideMenu />
                <div className="main-content" id="main-content">
                    <div className="page-content"><Loader /></div>
                </div>
            </div>
        );
    }

    return (
        <>
            {showSessionPopup && <SessionPopup />}
            <ToastContainer position="bottom-center" autoClose={3000} />

            <SweetAlert
                show={showConvertConfirm}
                warning
                showCancel
                confirmBtnText="Yes, Convert"
                cancelBtnText="Cancel"
                confirmBtnBsStyle="primary"
                cancelBtnBsStyle="default"
                title="Are you sure you want to convert this case to a Learning Case?"
                focusCancelBtn
                onConfirm={confirmConvertToLearningCase}
                onCancel={() => setShowConvertConfirm(false)}
            />
            <SweetAlert
                show={convertSuccessPopup}
                success
                confirmBtnText="Ok"
                confirmBtnBsStyle="primary"
                title="Case converted to Learning Case successfully"
                onConfirm={() => setConvertSuccessPopup(false)}
            />

            <div id="layout-wrapper">
                <Header />
                <SideMenu />

                <div className="main-content user-management create-user" id="main-content">
                    <div className="page-content">
                        <div className="container-fluid">

                            {/* ── breadcrumb ─────────────────────────────────── */}
                            <div className="row mb-4 breadcrumb">
                                <div className="col-lg-12">
                                    <div className="d-flex align-items-center">
                                        <div className="flex-grow-1">
                                            <h4 className="mb-2 card-title">
                                                Biopsy Case
                                                {caseDetails?.pathrefno && <span className="ms-2 text-muted" style={{ fontSize: "14px" }}>#{caseDetails.pathrefno}</span>}
                                                {renderStatusBadge(caseDetails?.status)}
                                            </h4>
                                        </div>
                                        {caseDetails?.status !== "Converted to Learning Case" && (
                                            <button
                                                className="create-quiz-btn border-0 me-2"
                                                type="button"
                                                onClick={handleConvertToLearningCase}
                                                disabled={isConverting}
                                            >
                                                {isConverting ? "Converting…" : "Convert to Learning Case"}
                                            </button>
                                        )}
                                        <a onClick={() => { GetTimeActivity(); navigate("/biopsy/cases"); }} className="create-quiz-btn border-0" style={{ cursor: "pointer" }}>Back</a>
                                    </div>
                                </div>
                            </div>

                            <div className="row">
                                <div className="col-12">
                                    <div className="card">
                                        <div className="card-body p-4">

                                            {/* ── read-only form fields ─────────────── */}
                                            <div className="row col-lg-12">
                                                {readOnlyField("PATH REF NO", caseDetails?.pathrefno)}
                                                {readOnlyField("BIOPSY DATE", caseDetails?.biopsydate)}
                                                {readOnlyField("CLINIC REGISTRATION NUMBER", caseDetails?.clinicregistrationnumber)}
                                                {readOnlyField("PATIENT NAME", caseDetails?.patientname)}
                                                {readOnlyField("AGE", caseDetails?.age)}
                                                {readOnlyField("GENDER", caseDetails?.gender)}
                                                {readOnlyField("SPECIMEN TYPE", caseDetails?.specimentype)}
                                                {readOnlyField("SITE OF BIOPSY", caseDetails?.siteofbiopsy)}
                                                {readOnlyField("BIOPSY TYPE", caseDetails?.biopsyType?.label)}
                                                {readOnlyField("TEST NAME", caseDetails?.defaultBiopsyTest?.name)}
                                                {readOnlyField("COST (₹)", caseDetails?.defaultBiopsyTest?.cost)}
                                                {readOnlyField("ASSIGNED PATHOLOGIST", caseDetails?.assignedPathologistName)}

                                                {/* Clinical History textarea */}
                                                <div className="col-lg-12 col-md-12 col-sm-12">
                                                    <div className="mb-3 input-field">
                                                        <label className="form_label">CLINICAL HISTORY</label>
                                                        <textarea
                                                            className="form-control edit-cpc-textarea"
                                                            rows={6}
                                                            disabled
                                                            value={caseDetails?.clinicalhistory || ""}
                                                            style={{ height: "150px" }}
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                            {/* ══════════════════════════════════════ */}
                                            {/* ── 1. CLINICAL HISTORY PHOTOS ─────── */}
                                            {/* ══════════════════════════════════════ */}
                                            <div className="row col-lg-12 mt-4 upload-imgs">
                                                <div className="col-lg-12 align-items-start justify-content-between upload-img-section pe-0 me-0">
                                                    <label className="form_label col-12">CLINICAL HISTORY PHOTOS</label>
                                                </div>
                                                <div className="mt-2 mb-0 pe-0">
                                                    <div className="d-flex justify-content-start col-lg-12 clinical-photo-section gap-3 mt-3">
                                                        <div className="uploaded_images-grid mb-3 mt-2">
                                                            {caseDetails?.biopsyfiles?.length > 0 ? caseDetails.biopsyfiles.map((item, key) => {
                                                                const imgPath = `${imageCloudfront}${item.sourcepath?.replace(/ /g, "%20")}?auto=compress,format`;
                                                                return (
                                                                    <div className="thumb-box" key={key}>
                                                                        <div className="thumb-img cursor-pointer" onClick={() => {
                                                                            const serverUrls = (caseDetails.biopsyfiles || []).map((f) => `${imageCloudfront}${f.sourcepath?.replace(/ /g, "%20")}?auto=compress,format`);
                                                                            openClinicalPopup(serverUrls, key);
                                                                        }}>
                                                                            <img
                                                                                src={`${imgPath}&width=150`}
                                                                                alt="clinical"
                                                                                onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = deepZoom; }}
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                );
                                                            }) : <p className="text-muted small mb-0">No clinical photos uploaded.</p>}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* ══════════════════════════════════════ */}
                                            {/* ── 2. HISTOLOGY PHOTOS ────────────── */}
                                            {/* ══════════════════════════════════════ */}
                                            <div className="row col-lg-12 mt-4 upload-imgs">
                                                <div className="col-lg-12 align-items-start justify-content-between upload-img-section pe-0 me-0">
                                                    <label className="form_label col-12">HISTOLOGY PHOTOS</label>
                                                </div>

                                                {/* JPEG section */}
                                                <div className="mt-2 mb-0 pe-0">
                                                    <div className="d-flex justify-content-between histology-photo-section">
                                                        <p className="case-type-text">PNG, JPEG, JPG Files</p>
                                                    </div>
                                                    <div className="d-flex justify-content-start col-lg-12 histology-photo-section gap-2">
                                                        <div className="mb-1 mt-2 input-field deep-zoom-styles">
                                                            {caseDetails?.histologyImages?.filter((f) => getExtension(f.originalFilename || f.filename) === ".jpeg,.jpg,.png")
                                                                .map((file, key) => {
                                                                    const imgPath = `${imageCloudfront}${(file.sourcepath || "").replace(/ /g, "%20")}?auto=compress,format`;
                                                                    const showFailed = file.uploadStatus === "FAILED" || isStaleUpload(file) || checkStuckUploads(file);
                                                                    return (
                                                                        <div className="thumb-box" key={key}>
                                                                            <div
                                                                                className="thumb-img cursor-pointer"
                                                                                onClick={() => {
                                                                                    if (file.allow && (!file.dzistatus || file.dzistatus.toUpperCase() !== "COMPLETED")) return;
                                                                                    const viewable = (caseDetails.histologyImages || []).filter(
                                                                                        (f) => f.uploadStatus === "COMPLETED" && getExtension(f.originalFilename || f.filename) === ".jpeg,.jpg,.png" && (!f.allow || f.dzistatus?.toUpperCase() === "COMPLETED")
                                                                                    );
                                                                                    openDziPopup(file, viewable);
                                                                                }}
                                                                            >
                                                                                <img
                                                                                    src={file.thumbnailUrl ? `${imageCloudfront.replace(/\/$/, "")}${file.thumbnailUrl}?auto=compress,format&width=150` : imgPath}
                                                                                    alt={file.originalFilename}
                                                                                    onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = deepZoom; }}
                                                                                />
                                                                                {showFailed && (
                                                                                    <span className="material-symbols-outlined img_view errorIcon">
                                                                                        <img src={errorSvg} alt="Failed" />
                                                                                    </span>
                                                                                )}
                                                                                {file.uploadStatus === "INPROGRESS" && !showFailed && (
                                                                                    <span className="material-symbols-outlined img_view loadingIcon">
                                                                                        <img src={loadingIcon} alt="Uploading" />
                                                                                    </span>
                                                                                )}
                                                                                {file.uploadStatus === "COMPLETED" && (file.dzistatus === "INPROGRESS" || !file.dzistatus) && file.allow && (
                                                                                    <span className="material-symbols-outlined img_view loadingIcon">
                                                                                        <img src={progessIcon} alt="Processing" />
                                                                                    </span>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    );
                                                                })}
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* TIFF section */}
                                                <div className="row col-lg-12 mt-0 upload-imgs">
                                                    <div className="d-flex justify-content-between tiff-files-section">
                                                        <p className="case-type-text">TIFF Files</p>
                                                    </div>
                                                    <div className="d-flex justify-content-start col-lg-12 histology-photo-section gap-2">
                                                        <div className="mb-3 mt-2 input-field deep-zoom-styles">
                                                            {caseDetails?.histologyImages?.filter((f) => getExtension(f.originalFilename || f.filename) === ".tif,.tiff")
                                                                .map((file, key) => {
                                                                    const showFailed = file.uploadStatus === "FAILED" || isStaleUpload(file) || checkStuckUploads(file);
                                                                    return (
                                                                        <div className={`upload_images d-flex gap-2 align-items-start ${!file.thumbnailUrl ? "upload-static-img" : ""}`} key={key}>
                                                                            <div
                                                                                className="img cursor-pointer"
                                                                                style={{ position: "relative" }}
                                                                                onClick={() => {
                                                                                    if (file.dzistatus === "COMPLETED") {
                                                                                        const viewable = (caseDetails.histologyImages || []).filter(
                                                                                            (f) => f.uploadStatus === "COMPLETED" && getExtension(f.originalFilename || f.filename) === ".tif,.tiff" && f.dzistatus?.toUpperCase() === "COMPLETED"
                                                                                        );
                                                                                        openDziPopup(file, viewable);
                                                                                    }
                                                                                }}
                                                                            >
                                                                                <span className={`default-img ${!file.thumbnailUrl ? "default-static-img" : ""}`}>
                                                                                    <img
                                                                                        src={file.thumbnailUrl ? `${imageCloudfront.replace(/\/$/, "")}${file.thumbnailUrl}?auto=compress,format&width=150` : deepZoom}
                                                                                        alt={file.originalFilename}
                                                                                    />
                                                                                </span>
                                                                                {showFailed && (
                                                                                    <span className="material-symbols-outlined img_view errorIcon">
                                                                                        <img src={errorSvg} alt="Failed" />
                                                                                    </span>
                                                                                )}
                                                                                {file.uploadStatus === "INPROGRESS" && !showFailed && (
                                                                                    <span className="material-symbols-outlined img_view loadingIcon">
                                                                                        <img src={loadingIcon} alt="Uploading" />
                                                                                    </span>
                                                                                )}
                                                                                {file.uploadStatus === "COMPLETED" && (file.dzistatus === "INPROGRESS" || !file.dzistatus) && (
                                                                                    <span className="material-symbols-outlined img_view loadingIcon">
                                                                                        <img src={progessIcon} alt="Processing" />
                                                                                    </span>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    );
                                                                })}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>


                                            {/* ══════════════════════════════════════ */}
                                            {/* ── 3. ADDITIONAL TESTS ────────────── */}
                                            {/* ══════════════════════════════════════ */}
                                            {caseDetails?.additionalTests?.length > 0 && (
                                                <div className="row col-lg-12 mt-4">
                                                    <div className="col-lg-8 col-md-8 col-sm-12">
                                                        <label className="form_label">ADDITIONAL TESTS</label>
                                                        <table className="table table-bordered">
                                                            <thead style={{ background: "#f8f9fa" }}>
                                                                <tr>
                                                                    <th style={{ fontWeight: "600", fontSize: "13px" }}>Test Name</th>
                                                                    <th style={{ fontWeight: "600", fontSize: "13px" }}>Fee (₹)</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {caseDetails.additionalTests.map((t, index) => (
                                                                    <tr key={`${t.name}-${index}`}>
                                                                        <td style={{ fontSize: "13px" }}>{t.name}</td>
                                                                        <td style={{ fontSize: "13px" }}>{t.fee}</td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                            <tfoot>
                                                                <tr style={{ background: "#f8f9fa" }}>
                                                                    <td style={{ fontWeight: "700", fontSize: "13px" }}>Total Amount</td>
                                                                    <td style={{ fontWeight: "700", fontSize: "13px" }}>₹ {totalAdditionalFee}</td>
                                                                </tr>
                                                            </tfoot>
                                                        </table>
                                                    </div>
                                                </div>
                                            )}

                                            {/* ══════════════════════════════════════ */}
                                            {/* ── 4. PATHOLOGIST FINDINGS ────────── */}
                                            {/* ══════════════════════════════════════ */}
                                            <div className="row col-lg-12 mt-2">
                                                <div className="col-lg-12 col-md-12 col-sm-12">
                                                    <div className="mb-3 input-field">
                                                        <label className="form_label">DIAGNOSIS</label>
                                                        <textarea
                                                            className="form-control edit-cpc-textarea"
                                                            rows={4}
                                                            disabled
                                                            value={caseDetails?.diagnosis || ""}
                                                            style={{ height: "100px" }}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="col-lg-12 col-md-12 col-sm-12">
                                                    <div className="mb-3 input-field">
                                                        <label className="form_label">HISTOPATHOLOGY FINDINGS</label>
                                                        <textarea
                                                            className="form-control edit-cpc-textarea"
                                                            rows={4}
                                                            disabled
                                                            value={caseDetails?.histopathologyFindings || ""}
                                                            style={{ height: "100px" }}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="col-lg-12 col-md-12 col-sm-12">
                                                    <div className="mb-3 input-field">
                                                        <label className="form_label">NOTES / REMARKS</label>
                                                        <textarea
                                                            className="form-control edit-cpc-textarea"
                                                            rows={4}
                                                            disabled
                                                            value={caseDetails?.notes || ""}
                                                            style={{ height: "100px" }}
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                        </div>
                                    </div>
                                </div>
                            </div>

                        </div>
                    </div>
                    <Footer />
                </div>
            </div>

            {/* ── clinical image lightbox ──────────────────────────────────────── */}
            {showImagePopup && (
                <div
                    style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100%", backgroundColor: "rgba(0,0,0,0.85)", zIndex: 1003, display: "flex", alignItems: "center", justifyContent: "center", padding: "20px" }}
                    onClick={() => setShowImagePopup(false)}
                >
                    <div style={{ position: "relative", maxWidth: "90vw", maxHeight: "90vh" }} onClick={(e) => e.stopPropagation()}>
                        {isClinicalLoading && <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}><Loader /></div>}
                        <img src={popupImageUrl} style={{ maxWidth: "85vw", maxHeight: "85vh", objectFit: "contain", display: "block" }} onLoad={() => setIsClinicalLoading(false)} alt="clinical" />
                        <button onClick={() => setShowImagePopup(false)} style={{ position: "absolute", top: "-10px", right: "-10px", background: "white", border: "none", borderRadius: "50%", width: "30px", height: "30px", cursor: "pointer", fontWeight: "bold" }}>×</button>
                        {clinicalPopupList.length > 1 && (
                            <>
                                <button onClick={() => navigateClinicalImage(clinicalPopupIndex - 1)} disabled={clinicalPopupIndex === 0} style={{ position: "absolute", left: "-40px", top: "50%", transform: "translateY(-50%)", background: "rgba(255,255,255,0.2)", border: "none", color: "#fff", fontSize: "24px", cursor: "pointer", borderRadius: "4px", padding: "4px 8px" }}>‹</button>
                                <button onClick={() => navigateClinicalImage(clinicalPopupIndex + 1)} disabled={clinicalPopupIndex === clinicalPopupList.length - 1} style={{ position: "absolute", right: "-40px", top: "50%", transform: "translateY(-50%)", background: "rgba(255,255,255,0.2)", border: "none", color: "#fff", fontSize: "24px", cursor: "pointer", borderRadius: "4px", padding: "4px 8px" }}>›</button>
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* ── DZI popup (non-DZI / small image with zoom-pan-pinch) ─────── */}
            {isPopupOpen && !dziUrl && (
                <div style={{ position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", backgroundColor: "rgba(0,0,0,0.5)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 999 }} onClick={() => setPopupOpen(false)}>
                    <div className="popup_wrp" style={{ position: "relative", background: "white", padding: "10px", borderRadius: "10px", maxWidth: "90vw", maxHeight: "90vh" }} onClick={(e) => e.stopPropagation()}>
                        <button style={{ position: "absolute", top: 10, right: 10, background: "white", color: "#000", border: "none", cursor: "pointer", borderRadius: "5px" }} onClick={() => setPopupOpen(false)}>
                            <span className="material-symbols-outlined">close</span>
                        </button>
                        <TransformWrapper ref={transformRef} initialScale={1}>
                            <div style={{ textAlign: "left", marginBottom: "10px" }}>
                                <button onClick={() => transformRef.current.zoomIn()} className="btnzoom"><span className="material-symbols-outlined">add</span></button>
                                <button onClick={() => transformRef.current.zoomOut()} className="btnzoom"><span className="material-symbols-outlined">remove</span></button>
                                <button onClick={() => transformRef.current.resetTransform()} className="btnzoom"><span className="material-symbols-outlined">refresh</span></button>
                            </div>
                            <TransformComponent>
                                {isPopupLoading && <div style={{ width: "400px", height: "400px" }}><Loader /></div>}
                                <LazyLoadImage src={dzipopupImage} effect="blur" onLoad={() => setPopupLoading(false)} style={{ width: "100%", borderRadius: "10px" }} />
                            </TransformComponent>
                        </TransformWrapper>
                    </div>
                </div>
            )}

            {/* ── DZI popup (OpenSeadragon full-screen) ────────────────────── */}
            {isPopupOpen && dziUrl && (
                <div className="deep_zoom">
                    <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", zIndex: 999 }}>
                        <div style={{ width: "98vw", height: "98vh", position: "relative", backgroundColor: "black", overflow: "hidden" }}>

                            {/* header bar */}
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "0 20px", height: "52px", background: "linear-gradient(135deg, #091918 0%, #215250 100%)", borderBottom: "1px solid rgba(255,255,255,0.08)", boxShadow: "0 2px 12px rgba(0,0,0,0.4)" }}>
                                <div style={{ width: "120px" }} />

                                {dziImageList.length > 1 ? (
                                    <div style={{ display: "flex", alignItems: "center", gap: "0", background: "rgba(255,255,255,0.07)", border: "1px solid rgba(255,255,255,0.15)", borderRadius: "30px", padding: "3px" }}>
                                        <button onClick={() => navigateDziImage(dziCurrentIndex - 1)} disabled={dziCurrentIndex === 0} style={{ border: "none", background: dziCurrentIndex === 0 ? "transparent" : "rgba(255,255,255,0.1)", color: dziCurrentIndex === 0 ? "rgba(255,255,255,0.25)" : "#fff", cursor: dziCurrentIndex === 0 ? "not-allowed" : "pointer", width: "34px", height: "34px", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                            <span className="material-symbols-outlined" style={{ fontSize: "20px" }}>chevron_left</span>
                                        </button>
                                        <span style={{ display: "flex", alignItems: "center", gap: "4px", padding: "0 12px", fontSize: "13px", fontWeight: "600", color: "#fff", userSelect: "none" }}>
                                            <span style={{ color: "#EA7F4A", fontWeight: "700" }}>{dziCurrentIndex + 1}</span>
                                            <span style={{ color: "rgba(255,255,255,0.35)", fontSize: "11px" }}>/</span>
                                            <span style={{ color: "rgba(255,255,255,0.6)" }}>{dziImageList.length}</span>
                                        </span>
                                        <button onClick={() => navigateDziImage(dziCurrentIndex + 1)} disabled={dziCurrentIndex === dziImageList.length - 1} style={{ border: "none", background: dziCurrentIndex === dziImageList.length - 1 ? "transparent" : "rgba(255,255,255,0.1)", color: dziCurrentIndex === dziImageList.length - 1 ? "rgba(255,255,255,0.25)" : "#fff", cursor: dziCurrentIndex === dziImageList.length - 1 ? "not-allowed" : "pointer", width: "34px", height: "34px", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                            <span className="material-symbols-outlined" style={{ fontSize: "20px" }}>chevron_right</span>
                                        </button>
                                    </div>
                                ) : <div />}

                                <div style={{ width: "120px", display: "flex", justifyContent: "flex-end" }}>
                                    <button className="dz_close" onClick={closeDeepZoom} style={{ border: "1px solid rgba(255,255,255,0.15)", background: "rgba(255,255,255,0.07)", color: "#fff", borderRadius: "50%", width: "34px", height: "34px", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
                                        <span className="material-symbols-outlined" style={{ fontSize: "18px" }}>close</span>
                                    </button>
                                </div>
                            </div>

                            {/* OSD viewer */}
                            <div style={{ height: "calc(95vh - 80px)", width: "100%", position: "relative" }}>
                                <div id="openseadragon-viewer" ref={viewerRef} style={{ height: "100%", width: "100%" }} />
                                {isDziLoading && (
                                    <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(0,0,0,0.55)", zIndex: 20, pointerEvents: "none" }}>
                                        <div style={{ width: "52px", height: "52px", borderRadius: "50%", border: "4px solid rgba(255,255,255,0.15)", borderTopColor: "#EA7F4A", animation: "dz-spin 0.75s linear infinite" }} />
                                        <style>{`@keyframes dz-spin { to { transform: rotate(360deg); } }`}</style>
                                    </div>
                                )}
                                <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)", color: "grey", fontSize: "2vw", fontWeight: "600", textTransform: "uppercase", letterSpacing: "2px", pointerEvents: "none", zIndex: 10, userSelect: "none" }}>
                                    Copyright © 2026 DADI
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
